import { Inject, Injectable } from '@angular/core';
import { BrowserStorage } from '../../../utils/shared-services/browser-storage.service';
import { Router } from '@angular/router';
import { HeadOfficePortalData } from '../models/head-office-portal.model';
import { View } from '../../store-portal/models/view.model';
import { HoWid001 } from '../widgets/components/wid-001';
import { HoWid002 } from '../widgets/components/wid-002';
import { HoWid0031 } from '../widgets/components/wid-003-1';
import { HoWid0032 } from '../widgets/components/wid-003-2';
import { HoWid004 } from '../widgets/components/wid-004';
import { HoWid005 } from '../widgets/components/wid-005';
import { HoWid010 } from '../widgets/components/wid-010';
import { HoWid021 } from '../widgets/components/wid-021';
import { HoWid022 } from '../widgets/components/wid-022';
import { HoWidgetInfo } from '../models/ho-widget.model';


@Injectable()
export class HOPortalService {
    public captions: any = {};
    private readonly ACCESS_TOKEN_KEY: string = 'accessToken';
    private readonly HO_PORTAL_DATA_KEY: string = 'hoPortalData';

    // information set corresponding to currently available widgets
    private readonly WIDGETS: Record<string, HoWidgetInfo> = {
        'wid-001': {
            title: 'Overview',
            datapoints: [],
            component: HoWid001,
        },
        'wid-002': {
            title: 'Net Sales & Final Sales',
            datapoints: ['Sales1_HO'],
            component: HoWid002,
        },
        'wid-003-1': {
            title: 'Hourly Sales Breakdown 1',
            datapoints: ['XCUBE_HOURLYTRANSHEADER_HO',],
            component: HoWid0031,
        },
        'wid-003-2': {
            title: 'Hourly Sales Breakdown 2',
            datapoints: ['XCUBE_HOURLYTRANSHEADER_HO',],
            component: HoWid0032,
        },
        'wid-004': {
            title: 'Top 5 Sales [Quantity]',
            datapoints: ['XCUBE_REVCPRODUCT1_HO'],
            component: HoWid004,
        },
        'wid-005': {
            title: 'Top 5 Sales [Net]',
            datapoints: ['XCUBE_REVCPRODUCT2_HO'],
            component: HoWid005,
        },
        'wid-010': {
            title: 'Message',
            datapoints: ['STORE_Info_Current'],
            component: HoWid010,
        },
        'wid-021': {
            title: 'Top 5 Payment Methods [Count]',
            datapoints: ['XCUBE_TENDER_TOP5_1_HO'],
            component: HoWid021,
        },
        'wid-022': {
            title: 'Top 5 Payment Methods [Amount]',
            datapoints: ['XCUBE_TENDER_TOP5_2_HO'],
            component: HoWid022,
        }
    };

    /*
 * This is the default configuration JSON which maintains the basic information and initial
 * state of a ho portal.
 */
    private VIEWS_CONFIG: View[] = [
        {
            id: 0,
            title: 'View 1',
            widgets: [
                { id: 'wid-001' },
                { id: 'wid-002' },
                { id: 'wid-003-1' },
                { id: 'wid-003-2' },
                { id: 'wid-004' },
                { id: 'wid-005' },
                { id: 'wid-010' },
                { id: 'wid-021' },
                { id: 'wid-022' },
            ],
        },
        {
            id: 1,
            title: 'View 2',
            widgets: [
                { id: 'wid-001' },
            ],
        },
        {
            id: 2,
            title: 'View 3',
            widgets: [{ id: 'wid-001' }, { id: 'wid-003-1' }],
        }
    ];

    constructor(
        @Inject(BrowserStorage) private browserStorage: BrowserStorage,
        @Inject(Router) private router: Router
    ) { }

    /**
     * @name getWidgetsInfo
     * @description gets a JSON object with details for every widget
     * @return information of all available widgets
     */
    getWidgetsInfo(): Record<string, HoWidgetInfo> {
        return this.WIDGETS;
    }

    /**
     * @name getAllWidgets
     * @description gets a parsed list of all widgets
     * @return all parsed available widgets
     */
    getAllWidgets(): { id: string }[] {
        return Object.keys(this.WIDGETS).map((key) => {
            return { id: key };
        });
    }

    /**
 * @name getDefaultPortalConfig
 * @description gets the default ho portal config (temporary for testing)
 * @return ho portal default config
 */
    getDefaultPortalConfig(): View[] {
        return this.VIEWS_CONFIG;
    }

    /**
     * @name clearAccessToken
     * @description clears the session storage for the access token
     */
    clearAccessToken() {
        this.browserStorage.removeSessionstorageItem(this.ACCESS_TOKEN_KEY);
    }

    /**
     * @name getHoPortalData
     * @description gets the ho portal data from session storage
     * @return saved ho portal data
     */
    getHoPortalData() {
        return this.browserStorage.getSessionstorage(this.HO_PORTAL_DATA_KEY, null);
    }

    /**
     * @name setHoPortalData
     * @description sets the ho portal data into session
     * @param data this is the ho portal data to be saved for the session
     */
    setHoPortalData(data: HeadOfficePortalData): void {
        this.browserStorage.setSessionstorage(this.HO_PORTAL_DATA_KEY, data);
    }

    /**
     * @name clearHoPortalData
     * @description clears the session storage for the ho portal data
     */
    clearHoPortalData() {
        this.browserStorage.removeSessionstorageItem(this.HO_PORTAL_DATA_KEY);
    }

    /**
     * @name logout
     * @description this method logs the user out and navigates them to the login page of the
     * inventory counter, and clears all saved data.
     */
    logout() {
        let alias = this.getHoPortalData().Alias;
        this.router.navigate(['/hoPortal/login'], {
            queryParams: {
                ...(alias ? { PortalID: alias } : {}),
            },
        });
        this.clearAccessToken();
        this.clearHoPortalData();
    }

    /**
     * @name parseSavedConfig
     * @description this method receives a ho portal config JSON that may be outdated and needs to
     * be updated to include any new changes made to the global config (add/remove views/widgets)
     * @param config this is the ho portal config JSON that will be parsed into the latest config
     * @return latest ho portal config
     */
    parseSavedConfig(savedConfig: View[]): View[] {
        if (!savedConfig) {
            return this.getDefaultPortalConfig();
        }
        for (const view of savedConfig) {
            let updatedWidgets = [];
            for (const widget of view.widgets) {
                if (this.WIDGETS[widget.id]) {
                    updatedWidgets.push(widget);
                }
            }
            view.widgets = updatedWidgets;
        }
        return savedConfig;
    }
}
